<template>
  <PrazoEncerrado v-if="!processoInscricao" />
  <v-row v-else>
    <v-col cols="12">
      <span class="grey--text text-h2"> {{ $route.name }} </span>
    </v-col>
    <v-col cols="12" class="px-0">
      <v-col cols="12">
        <CPTInput
          v-model="enrollment"
          v-maska="'###########'"
          autofocus
          label="Matrícula*"
          counter="11"
          @input="onInputEnrollment"
          @keydown.enter="isValidEnrollment && getStudent(enrollment)"
          type="number"
        />
      </v-col>
      <v-col cols="12 d-flex justify-end">
        <CPTBtn
          label="Buscar"
          icon="mdi-magnify"
          :disabled="!isValidEnrollment"
          :loading="loading"
          @click="getStudent(enrollment)"
        />
      </v-col>

      <v-skeleton-loader
        v-if="loading"
        :types="{ sections: 'heading, list-item-two-line@4' }"
        type="sections@2"
        class="mt-5"
      />

      <v-form
        v-else-if="student"
        @submit.prevent="onSubmit"
        ref="form"
        lazy-validation
        class="d-flex flex-wrap"
      >
        <!-- Student data -->
        <v-col cols="12">
          <CPTSubtitle label="Dados do aluno" />
        </v-col>

        <v-col cols="12">
          <CPTInput v-model="student.nome" label="Nome*" disabled required />
        </v-col>

        <v-col cols="12" md="6">
          <CPTInput
            v-model="dataNascimento"
            label="Data de Nascimento"
            disabled
          />
        </v-col>

        <v-col cols="12" md="6">
          <CPTInput v-model="student.cpfCnpj" label="CPF" disabled />
        </v-col>

        <v-col :cols="isProcessoJudicial && !isMobile ? 4 : 12" class="py-0">
          <v-switch
            v-model="isProcessoJudicial"
            @click="processoJudicial = null"
            label="Cadastro no Fila Única com processo judicial"
          />
        </v-col>

        <v-col v-if="isProcessoJudicial" :cols="isMobile ? 12 : 8" class="py-0">
          <CPTInput
            v-model="processoJudicial"
            label="Número do processo*"
            required
          />
        </v-col>

        <v-col cols="12" md="6" class="d-flex">
          <CPTFileInput
            v-model="vulnerabilidadeSocial"
            accept="image/*,.pdf"
            chips
            counter
            label="Criança em vulnerabilidade social (Documento do CRAS)"
            multiple
          />
        </v-col>

        <v-col cols="12" md="6" class="d-flex">
          <CPTFileInput
            v-model="acolhimentoInstitucional"
            accept="image/*,.pdf"
            chips
            counter
            label="Acolhimento institucional (Documento comprobatório)"
            multiple
          />
        </v-col>

        <v-col cols="12">
          <CPTTextArea
            v-model="observacaoInscricao"
            auto-grow
            label="Observação"
            outlined
          />
        </v-col>

        <v-col cols="12" md="6">
          <CPTSelect
            v-model="tipoInscricao"
            :items="TIPOS_INSCRICAO"
            item-text="text"
            item-value="value"
            required
            label="Tipo de Inscrição*"
            @input="onChangeTipoInscricao()"
          />
        </v-col>

        <template v-if="tipoInscricao === 'TRANSFERENCIA'">
          <v-col cols="12" md="6">
            <CPTFileInput
              v-model="anexosTransferencia"
              accept="image/*,.pdf"
              chips
              counter
              hint="Documentos que justifiquem essa movimentação"
              label="Anexos da Transferência*"
              multiple
              persistent-hint
              :rules="[
                (v) =>
                  (v && v.length > 0) ||
                  'Anexe ao menos um documento para transferências',
              ]"
            />
          </v-col>

          <v-col cols="12">
            <CPTTextArea
              v-model="observacaoTransferencia"
              auto-grow
              label="Observação da Transferência*"
              outlined
              required
            />
          </v-col>

          <v-col cols="12" md="6">
            <CPTAutocomplete
              v-model="unidadeTransferencia"
              :items="unidadesEnsino"
              itemText="nomeCompleto"
              label="Unidade Desejada para Transferência"
              noDataText="Nenhuma unidade encontrada"
            />
          </v-col>
        </template>

        <v-col cols="12" md="6">
          <CPTSelect
            v-model="transporteProprio"
            :items="MEIOS_TRANSPORTE"
            item-text="text"
            item-value="value"
            required
            label="Família possui meio de transporte próprio*"
          />
        </v-col>

        <v-col cols="12" class="d-flex flex-wrap">
          <v-col cols="12">
            <CPTSubtitle label="Endereço" />
          </v-col>

          <v-col cols="12" md="2">
            <CPTInput
              v-model="student.endereco.cep"
              label="CEP*"
              v-maska="'#####-###'"
              required
            />
          </v-col>
          <v-col cols="12" md="4">
            <CPTInput v-model="student.endereco.rua" label="Rua*" required />
          </v-col>
          <v-col cols="12" md="2">
            <CPTInput
              v-model="student.endereco.numero"
              v-maska="'#*'"
              label="Número"
            />
          </v-col>
          <v-col cols="12" md="4">
            <CPTInput
              v-model="student.endereco.bairro"
              label="Bairro*"
              required
            />
          </v-col>
          <v-col cols="12" md="6">
            <CPTAutocomplete
              v-model="state"
              label="Estado*"
              @change="getCities(true)"
              :items="states"
              noDataText="Estado não encontrado"
              item-text="nome"
              clearable
              return-object
              required
            />
          </v-col>
          <v-col cols="12" md="6">
            <CPTAutocomplete
              v-model="student.endereco.cidade"
              label="Cidade*"
              :items="cities"
              item-text="nome"
              noDataText="Cidade não encontrada"
              return-object
              clearable
              required
            />
          </v-col>
          <v-col cols="12">
            <CPTInput
              v-model="student.endereco.complemento"
              label="Complemento"
            />
          </v-col>
          <!-- /student data -->

          <v-col cols="12">
            <CPTSubtitle label="Membros da Família" />
          </v-col>

          <CPTFormMemberFamily v-model="father" />

          <CPTSubtitle />

          <CPTFormMemberFamily
            v-if="mother"
            v-model="mother"
            removeButton
            @remove="mother = null"
          />

          <template>
            <v-row
              v-for="(member, index) in members"
              :key="`${member.nome}_${member.kinship}`"
            >
              <v-col cols="12">
                <CPTSubtitle />
              </v-col>

              <CPTFormMemberFamily
                v-model="members[index]"
                removeButton
                @remove="members.splice(index, 1)"
              />
            </v-row>
          </template>

          <template v-if="newFamilyMember">
            <v-col cols="12">
              <CPTSubtitle />
            </v-col>
            <CPTFormMemberFamily
              v-model="newFamilyMember"
              removeButton
              @remove="newFamilyMember = null"
            />
          </template>

          <v-col class="d-flex justify-center" cols="12">
            <CPTBtn
              label="Adicionar Membro"
              icon="mdi-plus"
              :disabled="
                father.isEmpty() ||
                  (mother && mother.isEmpty()) ||
                  (newFamilyMember && newFamilyMember.isEmpty())
              "
              @click="addFamilyMember"
            />
          </v-col>

          <!--  Renda Familiar        -->
          <template v-if="family.length">
            <v-col cols="12">
              <CPTSubtitle label="Renda Familiar" />
            </v-col>

            <v-col cols="12" class="d-flex justify-left py-0 max-width">
              <v-dialog v-model="dialog" width="750">
                <v-card color="grey lighten-5">
                  <v-card-title class="text-h5 primary white--text">
                    ATENÇÃO! PER CAPITA ZERADA
                  </v-card-title>

                  <v-card-text class="pt-3">
                    Não foi atribuída renda para nenhum membro do grupo
                    familiar. Deseja continuar?
                  </v-card-text>

                  <v-divider />

                  <v-card-actions class="d-flex justify-end py-3">
                    <CPTBtn label="Não" @click="dialog = saving = false" />
                    <CPTBtn
                      label="Sim"
                      @click="
                        criarInscricaoComArquivos(inscricao), (dialog = false)
                      "
                    />
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
          </template>
          <v-container class="grey lighten-5">
            <v-row no-gutters>
              <v-col cols="15" sm="8" md="8">
                <!--  TABELA FAMILIAR       -->
                <v-card class="pa-10" outlined tile v-if="family.length">
                  <v-list-item three-line>
                    <v-list-item-content>
                      <div class="text-overline mb-4">
                        Tabela familiar
                      </div>
                      <v-list-item-subtitle>
                        <v-data-table
                          hide-default-footer
                          :headers="[
                            { sortable: false, value: 'number' },
                            {
                              text: 'Parentesco',
                              sortable: false,
                            },
                            {
                              text: 'Nome',
                              sortable: false,
                            },
                            {
                              text: 'Renda Mensal',
                              sortable: false,
                            },
                          ]"
                          :items="family"
                        >
                          <!--Minha Lista-->
                          <template v-slot:item="{ item: member, index }">
                            <tr v-if="index === 0">
                              <td>
                                <span
                                  class="text-subtitle-1"
                                  v-text="index + 1"
                                />
                              </td>

                              <td />

                              <td>
                                <span
                                  class="text-subtitle-1"
                                  v-text="student.nome"
                                />
                              </td>

                              <td>
                                R$ 0,00
                              </td>
                            </tr>

                            <tr>
                              <td>
                                <span
                                  class="text-subtitle-1"
                                  v-text="index + 2"
                                />
                              </td>
                              <td>
                                <span
                                  class="text-subtitle-1"
                                  v-text="member.kinship.nome"
                                />
                              </td>
                              <td>
                                <span
                                  class="text-subtitle-1"
                                  v-text="member.nome"
                                />
                              </td>

                              <td>
                                <v-text-field
                                  v-model="member.income"
                                  prefix="R$"
                                  :rules="salario"
                                  @input="
                                    (renda_atual = rendaPerCapta()),
                                      (income) => {
                                        if (income)
                                          member.income = income.replace(
                                            '.',
                                            ',',
                                          );
                                      }
                                  "
                                  class="text-subtitle-1"
                                  v-maska="'#*,##'"
                                />
                              </td>
                            </tr>
                          </template>
                          <!--Minha Lista-->
                        </v-data-table>
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-avatar tile size="70" color="white">
                      <v-icon size="70">mdi-human-male-female-child</v-icon>
                    </v-list-item-avatar>
                  </v-list-item>
                </v-card>
                <!--  TABELA FAMILIAR       -->
              </v-col>
              <v-col cols="8" md="4">
                <!--  Renda per capita       -->
                <v-card class="pa-2" outlined tile v-if="family.length">
                  <v-card-text>
                    <duvidas />
                    <div>Renda per capita</div>
                    <p class="text-h4 font-weight-black">
                      Valor Atual: {{ renda_atual || 0 }}
                    </p>
                  </v-card-text>
                </v-card>
                <!--  Renda per capita       -->
              </v-col>
            </v-row>
          </v-container>
        </v-col>
        <v-col cols="12 d-flex justify-end">
          <CPTBtn
            label="Salvar"
            :loading="saving"
            icon="mdi-content-save"
            @click="validate"
          />
          <DialogSalvar
            :dialog="dialogSave"
            :renda_atual="renda_atual"
            :student="student"
            :processoJudicial="processoJudicial"
            :members="members"
            :family="family"
            :vulnerabilidadeSocial="vulnerabilidadeSocial.length ? true : false"
            :acolhimentoInstitucional="
              acolhimentoInstitucional.length ? true : false
            "
            @close="dialogSave = false"
            @save="onSubmit"
          />
        </v-col>
      </v-form>
    </v-col>
    <!--  Renda Familiar        -->
  </v-row>
</template>

<script>
import DialogSalvar from "@/pages/Cadastro/Novo/DialogSalvar.vue";
import Duvidas from "@/pages/Cadastro/Novo/DialogFamiliar.vue";
import CPTInput from "@/components/Input";
import CPTSelect from "@/components/Select";
import CPTAutocomplete from "@/components/Autocomplete";
import CPTSubtitle from "@/components/FormSubtitle";
import CPTBtn from "@/components/Btn";
import CPTFormMemberFamily from "@/pages/Cadastro/components/FormMemberFamily";
import CPTFileInput from "@/components/FileInput";
import CPTTextArea from "@/components/TextArea";
import PrazoEncerrado from "@/pages/Cadastro/PrazoEncerrado";

import FamilyMember from "@/models/FamilyMember";
import Student from "@/models/Student";
import Inscricao from "@/models/Inscricao";

import validateTime from "@/utils/validateTime";
import certidaoAntiga from "@/utils/certidaoAntiga";
import { getAtribuicaoAtiva } from "@/plugins/security";
import { format, parseISO } from "date-fns";
import { first } from "lodash";

const MEIOS_TRANSPORTE = [
  { text: "Escolha uma opção", value: null },
  { text: "Não possui", value: "NAO_POSSUI" },
  { text: "Carro", value: "CARRO" },
  { text: "Moto", value: "MOTO" },
];

const TIPOS_INSCRICAO = [
  { text: "Cadastro", value: "CADASTRO" },
  { text: "Transferência", value: "TRANSFERENCIA" },
];

const SALARIO_MINIMO = 1621.0;

export default {
  name: "novo-cadastro",
  components: {
    CPTInput,
    CPTSelect,
    CPTAutocomplete,
    CPTBtn,
    CPTSubtitle,
    CPTFormMemberFamily,
    PrazoEncerrado,
    Duvidas,
    DialogSalvar,
    CPTFileInput,
    CPTTextArea,
  },

  mounted() {
    this.getProcessoInscricao();

    this.getStates();
  },

  data: () => ({
    SALARIO_MINIMO,
    MEIOS_TRANSPORTE,
    TIPOS_INSCRICAO,
    renda_atual: null,
    enrollment: null,
    loading: false,
    saving: false,
    dialog: false,
    dialogSave: false,
    valid: true,
    isProcessoJudicial: false,
    processoJudicial: null,
    vulnerabilidadeSocial: [],
    acolhimentoInstitucional: [],
    transporteProprio: null,
    tipoInscricao: "CADASTRO",
    observacaoTransferencia: null,
    anexosTransferencia: [],
    observacaoInscricao: null,
    inscricao: null,
    student: null,
    processoInscricao: null,
    state: null,
    cities: [],
    father: new FamilyMember(),
    mother: new FamilyMember(),
    newFamilyMember: null,
    members: [],
    salario: [(v) => !!v || "Renda Mensal é obrigatória!"],
    unidadesEnsino: [],
    unidadeTransferencia: null,
  }),

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    states() {
      return this.$store.state.address.states;
    },

    isValidEnrollment() {
      return this.enrollment?.toString()?.length === 11;
    },

    family() {
      let family = [];

      !this.father.isEmpty() && family.push(this.father);

      this.mother instanceof FamilyMember &&
        !this.mother.isEmpty() &&
        family.push(this.mother);

      family.push(...this.members);

      this.newFamilyMember instanceof FamilyMember &&
        !this.newFamilyMember.isEmpty() &&
        family.push(this.newFamilyMember);

      return family.filter((member) => member.nome && member.kinship);
    },

    dataNascimento() {
      return this.student.dataNascimento
        ? format(parseISO(this.student.dataNascimento), "dd/MM/yyyy")
        : null;
    },
  },

  methods: {
    validate() {
      if (!this.$refs.form.validate()) {
        return this.showMessage("Verifique os campos obrigatórios!", "warning");
      }

      if (
        this.tipoInscricao === "TRANSFERENCIA" &&
        !this.anexosTransferencia.length
      ) {
        return this.showMessage(
          "Para transferências, é obrigatório anexar ao menos um documento",
          "error",
        );
      }

      if (!this.family.length) {
        return this.showMessage(
          "O grupo familiar deve possuir ao menos um membro além do aluno",
          "error",
        );
      }

      this.dialogSave = true;
    },

    async onChangeTipoInscricao() {
      if (
        this.tipoInscricao === "TRANSFERENCIA" &&
        !this.unidadesEnsino.length
      ) {
        this.unidadesEnsino = await this.$erudio
          .get("unidades-ensino")
          .catch((error) => this.showMessage(error, "error"));
      }
    },

    addFamilyMember() {
      this.newFamilyMember && this.members.push(this.newFamilyMember);

      this.newFamilyMember = new FamilyMember();

      this.$refs.form.resetValidation();
    },

    // Não utilizado - mantido para referencias futuras
    criarInscricao(inscricao) {
      const atribuicaoID = getAtribuicaoAtiva().id;
      this.$http
        .post("inscricao", inscricao, { params: { atribuicaoID } })
        .then((inscricao) => {
          this.$router.push(`/protocolo/${inscricao.id}`);
        })
        .catch((error) => {
          this.showMessage(error, "error");
          this.saving = false;
        });
    },

    async criarInscricaoComArquivos(inscricao) {
      const atribuicaoID = getAtribuicaoAtiva().id;
      const formData = new FormData();
      formData.append("inscricao", JSON.stringify(inscricao));

      const categoriasDocumentos = {
        declaracao_vulnerabilidade_social: this.vulnerabilidadeSocial,
        anexo_transferencia: this.anexosTransferencia,
        declaracao_acolhimento_institucional: this.acolhimentoInstitucional,
      };

      Object.entries(categoriasDocumentos).forEach(([nomeCampo, arquivos]) => {
        (arquivos || []).forEach((arquivo) => {
          formData.append("arquivos", arquivo);
          formData.append("arquivos_nomes", nomeCampo);
        });
      });

      this.$http
        .post("inscricao", formData, {
          params: { atribuicaoID },
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((inscricao) => {
          this.$router.push(`/protocolo/${inscricao.id}`);
        })
        .catch((error) => {
          this.showMessage(error, "error");
          this.saving = false;
        });
    },

    async getCities(clearStudentCity) {
      if (clearStudentCity) {
        this.student.endereco.cidade = null;
        this.$refs.form.resetValidation();
      }

      if (!this.state) return;

      this.cities = await this.$http
        .get(`cidade?estado_id=${this.state.id}`)
        .catch((error) => this.showMessage(error, "error"));
    },

    async getProcessoInscricao() {
      this.loading = true;

      const processosAtivos = await this.$http
        .get("processo-inscricao")
        .catch((error) => this.showMessage(error, "error"));

      this.processoInscricao = first(
        processosAtivos.filter((processo) => !processo.encerrado),
      );

      this.loading = false;
    },

    getStates() {
      if (!this.states) this.$store.dispatch("address/getStates");
    },

    async getStudent(codigo) {
      this.loading = true;

      if (await this.hasInscricao(codigo)) {
        this.loading = false;
        return this.showMessage(
          "Já existe uma inscrição para essa matrícula",
          "error",
        );
      }

      const atribuicaoID = getAtribuicaoAtiva().id;
      this.$http
        .get("matricula", { params: { codigo, atribuicaoID } })
        .then(async (student) => {
          const { particularidades } = await this.$erudio
            .get(`pessoas/${student.pessoa.id}`)
            .catch((error) => this.showMessage(error, "error"));

          student.particularidades = !!particularidades.length;
          this.student = new Student(student);

          this.getStates();
          this.state = this.student.endereco.cidade.estado;

          this.getCities();
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.showMessage(error, "error");
        });
    },

    async hasInscricao(enrollment) {
      const inscricao = await this.$http
        .get(`matricula/${enrollment}/inscricao?hasInscricao=true`)
        .catch((error) => this.showMessage(error, "error"));

      return !!inscricao;
    },

    handleRendaPerCaptaZerada(inscricao) {
      this.inscricao = inscricao;

      this.dialog = true;
    },

    onInputEnrollment() {
      this.student = null;
      this.transporteProprio = null;
      this.tipoInscricao = "CADASTRO";
      this.observacaoTransferencia = null;
      this.anexosTransferencia = [];
      this.observacaoInscricao = null;
      this.mother?.clear();
      this.father?.clear();
      this.newFamilyMember?.clear();
      this.members = [];
      this.renda_atual = null;
      this.acolhimentoInstitucional = [];
    },

    onSubmit() {
      this.saving = true;
      if (!this.$refs.form.validate()) {
        this.saving = false;

        return this.showMessage(
          "Verifique as informações inseridas e tente novamente",
          "error",
        );
      }

      const family = this.family.map((member) => ({
        ...member,
        income: Number(member.income?.replace(",", ".")) || 0,
        endereco: member.endereco.isEmpty() ? null : member.endereco,
        certidaoNascimento:
          member.certidaoNascimento ||
          certidaoAntiga.certidaoNascimentoAntiga(member),
      }));

      const inscricao = new Inscricao(
        this.student.matricula.id,
        this.student.endereco,
        this.student.particularidades,
        family,
        this.transporteProprio,
        this.vulnerabilidadeSocial.length ? true : false,
        this.acolhimentoInstitucional.length ? true : false,
        this.processoJudicial,
        this.processoInscricao,
        this.observacaoInscricao,
        this.observacaoTransferencia,
        this.tipoInscricao,
        this.unidadeTransferencia,
      );

      const rendaAtual =
        this.family.reduce(
          (renda, member) => (renda += this.stringToCash(member.income)),
          0,
        ) /
        (this.family.length + 1);

      if (!rendaAtual) {
        return this.handleRendaPerCaptaZerada(inscricao);
      }

      // Se a renda per capita for menor que meio salario minimo, é considerado vulnerabilidade social pela normativa
      if (rendaAtual <= SALARIO_MINIMO / 2) {
        inscricao.vulnerabilidade_social = true;
      }

      this.criarInscricaoComArquivos(inscricao);
    },

    rendaPerCapta() {
      const rendaPerCapta =
        this.family.reduce(
          (renda, member) => (renda += this.stringToCash(member.income)),
          0,
        ) /
        (this.family.length + 1);

      return rendaPerCapta.toLocaleString("pt-br", {
        minimumFractionDigits: 2,
        style: "currency",
        currency: "BRL",
      });
    },
    stringToCash(string) {
      if (typeof string === "number") return string;

      return string ? Number(string.replace(",", ".")) : 0;
    },
    validateTime,
  },
};
</script>
